import { useMemo } from "react";
import { usePlanFeatures } from "@shared/queryHooks/useBilling";
import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";
import { PlanFeatures, Plans } from "@shared/types/organization";

interface PlanFeatureData {
  plan: string;
  jobLimit: number;
  deniedFeatures: string[];
  legacyPlan: boolean;
}

interface FeatureInfo {
  isLocked: boolean;
  canAccess: boolean;
  upgradePlanKey: string | null;
  planDisplayName: string;
  featureDisplayName: string;
  isGated: boolean;
  isLoading: boolean;
}

interface FeatureGateResult {
  getFeatureInfo: (feature: PlanFeatures) => FeatureInfo;
  isLoading: boolean;
}

/**
 * Simple hook for checking feature access based on organization's plan
 *
 * @returns Object with getFeatureInfo function and loading state
 *
 * @example
 * ```tsx
 * const { getFeatureInfo } = useFeatureGate();
 *
 * // Get all feature info in one call
 * const messagingFeature = getFeatureInfo(PlanFeatures.CANDIDATE_MESSAGING);
 *
 * // Use the comprehensive feature object
 * const isDisabled = messagingFeature.isLocked;
 * const upgradePlan = messagingFeature.upgradePlanKey;
 * const planName = messagingFeature.planDisplayName;
 * ```
 */
export function useFeatureGate(): FeatureGateResult {
  const { currentOrganization } = useCurrentSession();
  const { data: planFeaturesData, isLoading } = usePlanFeatures({
    refetchOnWindowFocus: false,
  });

  const result = useMemo(() => {
    if (!planFeaturesData?.plans || !currentOrganization) {
      const getFeatureInfo = (): FeatureInfo => ({
        isLocked: true,
        canAccess: false,
        upgradePlanKey: null,
        planDisplayName: "",
        featureDisplayName: "",
        isGated: false,
        isLoading,
      });

      return {
        getFeatureInfo,
        isLoading,
      };
    }

    const plans = planFeaturesData.plans;
    const currentPlan = currentOrganization?.stripeSubscriptionInGoodStanding
      ? currentOrganization.plan
      : "plan_no_plan";
    const currentPlanData = plans.find((p) => p.plan === currentPlan) || null;

    // Get features denied for the current plan
    const deniedFeatures = currentPlanData?.deniedFeatures;

    // Helper functions moved inside the hook
    const getPlanDisplayName = (planKey: string): string => {
      const planNames: Record<string, string> = {
        [Plans.SIMPLE_ATS_FREE]: "Free",
        [Plans.ATS_TIER_FREE]: "Free",
        [Plans.ATS_TIER_STARTER]: "Starter",
        [Plans.ATS_TIER_GROWTH]: "Growth",
        [Plans.ATS_TIER_SCALE]: "Scale",
        [Plans.ATS_TIER_ENTERPRISE]: "Enterprise",
        [Plans.SIMPLE_ATS_PAID]: "Legacy Paid",
        [Plans.SIMPLE_ATS_PER_JOB]: "Legacy Per Job",
        [Plans.ATS_TIER_APOLLO]: "Legacy Apollo",
      };

      return planNames[planKey] || planKey;
    };

    const getFeatureDisplayName = (featureKey: string): string => {
      const featureNames: Record<string, string> = {
        [PlanFeatures.CANDIDATE_MESSAGING]: "Candidate Messaging",
        [PlanFeatures.CUSTOM_DOMAIN]: "Custom Domain",
        [PlanFeatures.CUSTOM_CONFIRMATION_EMAILS]: "Custom Application Confirmation Emails",
        [PlanFeatures.IMPORT_CANDIDATES]: "Import Candidates",
        [PlanFeatures.LINKEDIN_FEED]: "LinkedIn Integration",
        [PlanFeatures.SLACK_INTEGRATION]: "Slack Integration",
        [PlanFeatures.DISCORD_INTEGRATION]: "Discord Integration",
        [PlanFeatures.GOOGLE_JOBS_INTEGRATION]: "Google Jobs Integration",
        [PlanFeatures.HIRING_STAGE_MESSAGE_AUTOMATIONS]: "Stage Message Automation",
        [PlanFeatures.BULK_MOVE]: "Bulk Move Candidates",
        [PlanFeatures.BULK_MESSAGE]: "Bulk Message Candidates",
        [PlanFeatures.X_HIRING_FEED]: "X Hiring Integration",
        [PlanFeatures.REQUEST_REVIEW]: "Request Review",
        [PlanFeatures.INTERVIEWER_ROLE]: "Interviewer Role",
        [PlanFeatures.WHITE_LABEL_JOB_BOARD]: "White Label Job Board",
        [PlanFeatures.BULK_RESUME_EXPORT]: "Bulk Resume Export",
      };

      return featureNames[featureKey] || featureKey;
    };

    // Get the first plan (non-legacy) whose denylist excludes the feature (i.e., a plan that grants it)
    const getFirstPlanWithFeature = (feature: PlanFeatures): string | null => {
      const planWithFeature = plans
        .filter((p) => !p.legacyPlan && !p.deniedFeatures.includes(feature))
        .sort((a, b) => a.jobLimit - b.jobLimit)[0]; // Sort by job limit to get lowest tier first

      return planWithFeature?.plan || null;
    };

    const getFeatureInfo = (feature: PlanFeatures): FeatureInfo => {
      const canAccess = !deniedFeatures.includes(feature);
      const isLocked = !canAccess;
      const upgradePlanKey = getFirstPlanWithFeature(feature);
      const planDisplayName = upgradePlanKey ? getPlanDisplayName(upgradePlanKey) : "Paid";
      const featureDisplayName = getFeatureDisplayName(feature);
      const isGated = upgradePlanKey !== null;

      return {
        isLocked,
        canAccess,
        upgradePlanKey,
        planDisplayName,
        featureDisplayName,
        isGated,
        isLoading,
      };
    };

    return {
      getFeatureInfo,
      isLoading,
    };
  }, [planFeaturesData, currentOrganization, isLoading]);

  return result;
}
